import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Calculator } from '@/components/Calculator';
import {
  Concern, chooseTarget, requiredCfm, toM3h, fmt, fmtRequiredCfm, fmtRequiredM3h, sqftToSqm, ftToM,
} from '@/lib/calculator';
import { canonical } from '@/lib/site';

interface Props {
  path: string;
  title: string;
  h1: string;
  description: string;
  areaSqFt: number;
  ceilingFt: number;
  concerns: Concern[];
  intro?: ReactNode;
  notes: string[];
  children?: ReactNode;
}

/** Shared layout for SEO entry pages. Every number comes from the calculator functions. */
export function SizingLanding({ path, title, h1, description, areaSqFt, ceilingFt, concerns, intro, notes, children }: Props) {
  const target = chooseTarget(concerns);
  const room = { areaSqFt, ceilingFt };
  const req = target ? requiredCfm(room, target.each) : null;
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical(path)} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical(path)} />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">Calculator</Link> / <Link to="/guides" className="hover:text-foreground">Room guides</Link>
        </nav>
        <h1 className="text-3xl font-bold text-foreground">{h1}</h1>
        {intro && <div className="text-muted-foreground mt-3">{intro}</div>}
        {target && req != null && (
          <div className="rounded-lg bg-muted p-5 mt-6">
            <p className="text-sm text-muted-foreground">{areaSqFt} ft² ({fmt(sqftToSqm(areaSqFt), 1)} m²), {ceilingFt} ft ({fmt(ftToM(ceilingFt), 2)} m) ceiling, {target.label}</p>
            <p className="text-3xl font-bold text-foreground mt-1">{fmtRequiredCfm(req)} <span className="text-base font-normal text-muted-foreground">({fmtRequiredM3h(toM3h(req))})</span></p>
            <p className="text-sm text-muted-foreground mt-2 font-mono">{fmt(areaSqFt * ceilingFt)} ft³ × {target.each} ÷ 60 = {fmt(req, 1)} CFM</p>
            <p className="text-xs text-muted-foreground mt-2">{target.basis}</p>
          </div>
        )}
        {concerns.includes('gases') && (
          <p className="text-sm text-foreground mt-4">Odours and gases are a separate problem from particles — particle CADR says nothing about them. <Link to="/blog/odours-gases-and-vocs" className="text-primary underline">Read the gases guide</Link>.</p>
        )}
        {notes.length > 0 && (
          <section className="mt-8">
            <h2 className="text-xl font-bold text-foreground mb-2">Things worth knowing</h2>
            <ul className="list-disc pl-5 space-y-2 text-muted-foreground">{notes.map((n) => <li key={n}>{n}</li>)}</ul>
          </section>
        )}
        {children}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-foreground mb-2">Check your exact room and see matching setups</h2>
          <p className="text-sm text-muted-foreground mb-4">Pre-filled with the example above. Change anything, then calculate.</p>
          <Calculator initialRoom={room} initialConcerns={concerns} />
        </section>
      </main>
      <Footer />
    </>
  );
}
